import React, { useState } from 'react';
import {
  Download,
  FileArchive,
  Users,
  Briefcase,
  FileText,
  CreditCard,
  Receipt,
  UserPlus,
  Wrench,
  FileSignature,
  Info
} from 'lucide-react';
import api from '../../services/api';

const DataExportTab = ({ showToast }) => {
  const [downloadingKey, setDownloadingKey] = useState(null);

  const exportTypes = [
    { key: 'customers',          label: 'Customers',          description: 'All customer records with contact info and notes',                                            icon: Users },
    { key: 'jobs',               label: 'Jobs',               description: 'All jobs with scheduling, status, and amounts',                                               icon: Briefcase },
    { key: 'invoices',           label: 'Invoices',           description: 'All invoices with totals and payment status',                                                 icon: FileText },
    { key: 'payments',           label: 'Payments',           description: 'Individual payments applied to invoices',                                                     icon: CreditCard },
    { key: 'expenses',           label: 'Expenses',           description: 'Business expenses with tax categorization (Schedule C)',                                       icon: Receipt },
    { key: 'leads',              label: 'Leads',              description: 'Online leads captured from your public form',                                                 icon: UserPlus },
    { key: 'service-types',      label: 'Service Types',      description: 'Your service catalog and pricing',                                                            icon: Wrench },
    { key: 'signed-agreements',  label: 'Signed Agreements',  description: 'Customer agreements with audit trail (IP, timestamps, consents)',                             icon: FileSignature },
  ];

  const downloadFile = async (path, downloadKey) => {
    try {
      setDownloadingKey(downloadKey);

      const response = await api.get(`/dataexport/${path}`, {
        responseType: 'blob',
      });

      // Try to pull filename from Content-Disposition header
      let filename = path === 'all'
        ? `suddyflow-export-${new Date().toISOString().slice(0, 10)}.zip`
        : `suddyflow-${path}-${new Date().toISOString().slice(0, 10)}.csv`;

      const disposition = response.headers['content-disposition'];
      if (disposition) {
        const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
        if (match && match[1]) {
          filename = decodeURIComponent(match[1]);
        }
      }

      const contentType = path === 'all' ? 'application/zip' : 'text/csv';
      const blob = new Blob([response.data], { type: contentType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      if (showToast) {
        showToast(path === 'all' ? 'Full export downloaded' : 'Export downloaded');
      }
    } catch (err) {
      console.error('Export failed:', err);
      if (showToast) {
        showToast('Failed to export data. Please try again.', 'error');
      }
    } finally {
      setDownloadingKey(null);
    }
  };

  const isDisabled = downloadingKey !== null;

  return (
    <div className="space-y-6">
      {/* Intro card */}
      <div className="card">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Data Export</h2>
        <p className="text-sm text-gray-600">
          Download your business data as CSV files. You own your data — export it anytime for
          backups, accounting, or migration to another system.
        </p>
      </div>

      {/* Full ZIP export */}
      <div className="card">
        <div className="flex items-start justify-between gap-4 flex-col sm:flex-row">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-primary-50 rounded-lg flex-shrink-0">
              <FileArchive size={20} className="text-primary-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Full Export (ZIP)</h3>
              <p className="text-sm text-gray-600 mt-1">
                Download everything — all 8 data types in one ZIP archive with a README explaining each file.
              </p>
            </div>
          </div>
          <button
            onClick={() => downloadFile('all', 'all')}
            disabled={isDisabled}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap transition-colors"
          >
            <Download size={16} />
            {downloadingKey === 'all' ? 'Preparing…' : 'Download ZIP'}
          </button>
        </div>
      </div>

      {/* Individual CSV exports */}
      <div className="card">
        <div className="mb-4">
          <h3 className="font-semibold text-gray-900">Individual Exports</h3>
          <p className="text-sm text-gray-600 mt-1">
            Download a specific data type as a CSV file.
          </p>
        </div>

        <div className="divide-y divide-gray-200 -mx-6">
          {exportTypes.map((type) => {
            const Icon = type.icon;
            const isDownloading = downloadingKey === type.key;

            return (
              <div
                key={type.key}
                className="flex items-start justify-between gap-4 px-6 py-4 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-start gap-3 min-w-0">
                  <div className="p-2 bg-gray-100 rounded-lg flex-shrink-0">
                    <Icon size={16} className="text-gray-600" />
                  </div>
                  <div className="min-w-0">
                    <div className="font-medium text-gray-900">{type.label}</div>
                    <div className="text-sm text-gray-600">{type.description}</div>
                  </div>
                </div>
                <button
                  onClick={() => downloadFile(type.key, type.key)}
                  disabled={isDisabled}
                  className="flex items-center gap-2 px-3 py-1.5 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap transition-colors flex-shrink-0"
                >
                  <Download size={14} />
                  {isDownloading ? 'Downloading…' : 'CSV'}
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Info note */}
      <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-2">
        <Info size={16} className="text-blue-600 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-blue-700">
          All dates are in UTC. Files open in Excel, Google Sheets, Numbers, or any text editor.
          Large exports may take a few seconds to prepare.
        </p>
      </div>
    </div>
  );
};

export default DataExportTab;